import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchVisits } from 'store/visits/actions';
import { VisitUiType } from 'store/visits/types';
import { getPaginatedVisitsState, getAllFilters, getInvalidationToken } from 'store/visits/selectors';
import { useDebounce } from './use-debounce.hook';

interface UseInfiniteScrollVisitsParams {
  buildingId: string | undefined;
  type: VisitUiType;
  batchSize?: number;
  after?: string;
  before?: string;
}

interface UseInfiniteScrollVisitsReturn {
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  observerTarget: RefObject<HTMLElement>;
  error: Error | null;
  retry: () => void;
  nextOffset: number;
}

const DEBOUNCE_DELAY = 300;
const VISITS_LOADED_RESET_DELAY = 100;

export const useInfiniteScrollVisits = ({
  buildingId,
  type,
  batchSize = 50,
  after,
  before,
}: UseInfiniteScrollVisitsParams): UseInfiniteScrollVisitsReturn => {
  const dispatch = useDispatch();
  const { data: visits, total } = useSelector(getPaginatedVisitsState(type));
  const filters = useSelector(getAllFilters);
  const invalidationToken = useSelector(getInvalidationToken);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const observerTarget = useRef<HTMLElement>(null);
  const loadingRef = useRef(false);
  // The offset of the loadMore currently on the wire, so a deferred background refresh can tell when the
  // exact page it is waiting behind has landed (see the invalidation effects below). Null when idle.
  const inFlightOffsetRef = useRef<number | null>(null);

  // Memoize expensive calculations to avoid recalculating on every render
  const { nextOffset, loadedCount } = useMemo(() => {
    const indicesWithData = visits.map((v, i) => (v.data !== null ? i : -1)).filter((i) => i !== -1);
    const highest = indicesWithData.length > 0 ? Math.max(...indicesWithData) : -1;
    return {
      highestLoadedIndex: highest,
      nextOffset: highest + 1,
      loadedCount: indicesWithData.length,
    };
  }, [visits]);

  // Calculate if there are more items to load
  const hasMore = total !== null && nextOffset < total;

  // Store frequently changing values in refs to stabilize callback dependencies
  const nextOffsetRef = useRef(nextOffset);
  nextOffsetRef.current = nextOffset;

  const loadMoreInternal = useCallback(() => {
    if (!buildingId || !hasMore || loadingRef.current) {
      return;
    }

    const offset = nextOffsetRef.current;

    loadingRef.current = true;
    inFlightOffsetRef.current = offset;
    setIsLoadingMore(true);
    setError(null);

    dispatch(
      fetchVisits({
        type,
        buildingId,
        limit: batchSize,
        offset,
        ...(after && { after }),
        ...(before && { before }),
      }),
    );
  }, [buildingId, hasMore, type, batchSize, after, before, dispatch]);

  const loadMore = useDebounce(loadMoreInternal, DEBOUNCE_DELAY);

  const retry = useCallback(() => {
    setError(null);
    loadMoreInternal();
  }, [loadMoreInternal]);

  // Background refresh when the activity SSE stream reports a change elsewhere. Re-reads the currently
  // loaded window from offset 0 with the current filters as a BACKGROUND fetch — the shared spinner
  // ignores it, so no skeleton collapse, no navigation, and no scroll reset for a user on the list; a
  // user in the form or on a visit detail is untouched. Mutable inputs are read through a ref so it uses
  // current values without making the token effect below re-run on every render.
  const refreshParamsRef = useRef({ buildingId, type, batchSize, after, before, loadedCount });
  refreshParamsRef.current = { buildingId, type, batchSize, after, before, loadedCount };

  const runBackgroundRefresh = useCallback(() => {
    const { buildingId: b, type: t, batchSize: bs, after: a, before: bf, loadedCount: lc } = refreshParamsRef.current;
    // Nothing loaded yet (list never opened) — the list's own initial fetch will read fresh; skip.
    if (!b || lc === 0) {
      return;
    }
    dispatch(
      fetchVisits({
        type: t,
        buildingId: b,
        limit: Math.max(lc, bs),
        offset: 0,
        background: true,
        ...(a && { after: a }),
        ...(bf && { before: bf }),
      }),
    );
  }, [dispatch]);

  // A refresh that arrives while a loadMore is still on the wire is DEFERRED, not forced through. The
  // epics use `mergeMap`, so an offset-0 refresh dispatched now would race the pending page and — because
  // an offset-0 success rebuilds the array — could wipe the page the user just watched load. So queue it
  // and leave `loadingRef` set (the observer can't refire either), then run it once the page has landed.
  const pendingRefreshRef = useRef(false);
  const skipFirstInvalidation = useRef(true);
  useEffect(() => {
    if (skipFirstInvalidation.current) {
      skipFirstInvalidation.current = false;
      return;
    }
    if (loadingRef.current) {
      pendingRefreshRef.current = true;
      return;
    }
    runBackgroundRefresh();
  }, [invalidationToken, runBackgroundRefresh]);

  // Flush a deferred refresh once the page it was waiting behind has settled. The in-flight page's own
  // redux loading flag is the completion signal — the reducer clears it on success, on an empty page AND
  // on a failure alike — so a queued refresh is never stranded by an outcome the spinner reset (which
  // keys on newly-arrived rows) does not cover. `isLoadingMore` is a dependency too, not just `visits`:
  // the guard can clear (the reset timer flips `isLoadingMore` false) in a render where `visits` did not
  // change, and without it a refresh queued in that ~100ms window would sit forever waiting on a `visits`
  // change that, for an idle user, never comes.
  useEffect(() => {
    if (!pendingRefreshRef.current) {
      return;
    }
    const offset = inFlightOffsetRef.current;
    const stillInFlight = offset !== null && visits[offset]?.loading === true;
    if (stillInFlight) {
      return;
    }
    pendingRefreshRef.current = false;
    inFlightOffsetRef.current = null;
    runBackgroundRefresh();
  }, [visits, isLoadingMore, runBackgroundRefresh]);

  // Set up Intersection Observer
  useEffect(() => {
    const target = observerTarget.current;
    if (!target || !hasMore || !buildingId) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !loadingRef.current) {
          loadMore();
        }
      },
      {
        root: null, // viewport
        rootMargin: '200px', // Start loading 200px before reaching the bottom
        threshold: 0.1,
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadMore, buildingId]);

  // Reset loading state when filters or date range changes. Also drop any deferred refresh: it was
  // queued against the OLD window, the filter change fires its own fresh fetch, and leaving the refs set
  // would fire a stale, redundant offset-0 read on the next `visits` change.
  useEffect(() => {
    loadingRef.current = false;
    pendingRefreshRef.current = false;
    inFlightOffsetRef.current = null;
    setIsLoadingMore(false);
  }, [filters, after, before]);

  useEffect(() => {
    if (loadedCount === 0) {
      return undefined;
    }

    const timer = setTimeout(() => {
      if (loadingRef.current) {
        loadingRef.current = false;
        setIsLoadingMore(false);
      }
    }, VISITS_LOADED_RESET_DELAY);

    return () => clearTimeout(timer);
  }, [loadedCount]);

  return {
    hasMore,
    isLoadingMore,
    loadMore,
    observerTarget,
    error,
    retry,
    nextOffset,
  };
};
